import { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import axios from 'axios';
import { useAuth } from '../contexts/AuthContext';
import { ArrowLeft, Bell, MessageCircle, Star, Check, Trash2 } from 'lucide-react';

const API_URL = process.env.REACT_APP_BACKEND_URL;

const NOTIF_ICONS = {
  new_chat: MessageCircle,
  rating: Star,
};

const NOTIF_COLORS = {
  new_chat: '#E11D48',
  rating: '#F59E0B',
};

export default function Notifications() {
  const { user } = useAuth();
  const navigate = useNavigate();
  const [notifications, setNotifications] = useState([]);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    if (user) fetchNotifications();
  }, [user]);

  const fetchNotifications = async () => {
    try {
      const { data } = await axios.get(`${API_URL}/api/notifications`, { withCredentials: true });
      setNotifications(data);
    } catch (err) {
      console.error('Error fetching notifications:', err);
    } finally {
      setLoading(false);
    }
  };

  const markAllRead = async () => {
    try {
      await axios.put(`${API_URL}/api/notifications/read`, {}, { withCredentials: true });
      setNotifications(prev => prev.map(n => ({ ...n, read: true })));
    } catch (err) {
      console.error('Error marking read:', err);
    }
  };

  const handleClick = async (notif) => {
    if (!notif.read) {
      try {
        await axios.put(`${API_URL}/api/notifications/${notif.id}/read`, {}, { withCredentials: true });
        setNotifications(prev => prev.map(n => n.id === notif.id ? { ...n, read: true } : n));
      } catch (err) {
        console.error('Mark notification read failed:', err);
      }
    }
    if (notif.link) navigate(notif.link);
  };

  const unreadCount = notifications.filter(n => !n.read).length;

  if (!user) { navigate('/login'); return null; }

  return (
    <div className="min-h-screen bg-[#050505] pb-24 lg:pb-12">
      <div className="max-w-4xl mx-auto">
      <div className="sticky top-12 z-40 bg-[#050505]/95 backdrop-blur-sm border-b border-white/5 px-4 h-12 flex items-center justify-between">
        <div className="flex items-center gap-3">
          <button onClick={() => navigate(-1)} className="text-white" data-testid="notifications-back-btn">
            <ArrowLeft className="w-5 h-5" />
          </button>
          <Bell className="w-5 h-5 text-[#E11D48]" />
          <h1 className="font-bold text-white text-sm">Notifications</h1>
          {unreadCount > 0 && (
            <span className="text-xs text-[#E11D48] font-bold">({unreadCount})</span>
          )}
        </div>
        {unreadCount > 0 && (
          <button
            onClick={markAllRead}
            className="text-xs text-[#71717A] hover:text-white flex items-center gap-1 transition-colors"
            data-testid="mark-all-read-btn"
          >
            <Check className="w-3 h-3" /> Mark all read
          </button>
        )}
      </div>

      {loading ? (
        <div className="flex items-center justify-center py-20">
          <div className="w-8 h-8 border-2 border-[#E11D48] border-t-transparent rounded-full animate-spin" />
        </div>
      ) : notifications.length === 0 ? (
        <div className="flex flex-col items-center justify-center py-20 gap-3">
          <Bell className="w-12 h-12 text-[#71717A]" />
          <p className="text-[#71717A] text-sm">No notifications yet</p>
          <p className="text-xs text-[#555]">You'll be notified when people interact with your characters</p>
        </div>
      ) : (
        <div className="divide-y divide-white/5">
          {notifications.map((notif) => {
            const Icon = NOTIF_ICONS[notif.type] || Bell;
            const color = NOTIF_COLORS[notif.type] || '#E11D48';
            return (
              <button
                key={notif.id}
                onClick={() => handleClick(notif)}
                className={`w-full flex items-start gap-3 px-4 py-3 text-left transition-colors hover:bg-white/5 ${
                  !notif.read ? 'bg-[#E11D48]/5' : ''
                }`}
                data-testid={`notification-${notif.id}`}
              >
                <div className="flex-shrink-0 mt-0.5">
                  {notif.actor_avatar ? (
                    <img src={notif.actor_avatar} alt="" className="w-9 h-9 rounded-full object-cover border border-white/10" />
                  ) : (
                    <div className="w-9 h-9 rounded-full flex items-center justify-center" style={{ background: `${color}20` }}>
                      <Icon className="w-4 h-4" style={{ color }} />
                    </div>
                  )}
                </div>
                <div className="flex-1 min-w-0">
                  <p className={`text-sm ${!notif.read ? 'text-white font-medium' : 'text-[#A1A1AA]'}`}>
                    {notif.body}
                  </p>
                  <p className="text-[10px] text-[#555] mt-0.5">
                    {new Date(notif.created_at).toLocaleDateString('en-GB', { day: 'numeric', month: 'short', hour: '2-digit', minute: '2-digit' })}
                  </p>
                </div>
                {!notif.read && (
                  <div className="w-2 h-2 rounded-full bg-[#E11D48] flex-shrink-0 mt-2" />
                )}
              </button>
            );
          })}
        </div>
      )}
      </div>
    </div>
  );
}
